// Web mirror of the mobile app's Home placement rules
// (apps/client-mobile/src/screens/Home/components/homeFeedRows.ts). The gateway
// returns ONE ordered list of typed blocks (GET /feed/blocks); the mobile app
// is the canon for how they are placed, and the web reproduces that semantics
// one-to-one:
//
//   • `personalFeed` is a MARKER, not a visual block. Everything BEFORE it is
//     the header drawn above the product grid; everything AFTER it is
//     merchandising interleaved INTO the grid.
//   • the header composition and the interleave rhythm come from the app, the
//     order and the block contents come from the server.
import type { FeedBlockWire, FeedDepartment, FeedSegment, Product } from "@/api";

// Segment ⇄ root category: корни аудиторий приходят из дерева категорий
// (lib/category-tree.ts → segmentRootIds), витрина их не хардкодит.

// The three audience tabs, in order, with their RU labels. `womens` opens first.
// Labels follow the Figma redesign («Женщинам / Мужчинам / Детям»).
export const HOME_SEGMENTS: { key: FeedSegment; label: string }[] = [
	{ key: "womens", label: "Женщинам" },
	{ key: "mens", label: "Мужчинам" },
	{ key: "kids", label: "Детям" },
];

export const DEFAULT_HOME_SEGMENT: FeedSegment = "womens";

// ── Department axis («Одежда» / «Красота» / «Дом и интерьер») ─────────────
// Разделы шапки строятся из корней дерева категорий (lib/category-tree.ts →
// departmentsFromTree): «Одежда» — корни-аудитории (Женщинам / Мужчинам /
// Детям), каждый прочий корень — свой раздел. Ключ раздела — `clothes` или
// раздел витрины корня (`home`, `beauty`); он же хранится в настройках.
//
// /feed/blocks знает отделы clothes / beauty / home (миграция 135 завела
// стартовые блоки «Красоты»); для прочих разделов (новый корень из консоли)
// редакционные блоки берутся «одёжные», а лента товаров рутится корнем раздела.
export type Department = string;

export const DEFAULT_DEPARTMENT: Department = "clothes";

const FEED_DEPARTMENTS = new Set<FeedDepartment>(["clothes", "home", "beauty"]);

/** Отдел редакционной ленты для раздела шапки (контракт /feed/blocks). */
export function feedDepartmentOf(department: Department): FeedDepartment {
	return FEED_DEPARTMENTS.has(department as FeedDepartment) ? (department as FeedDepartment) : "clothes";
}

/**
 * How many products sit between two interleaved blocks. The app injects a block
 * every three grid rows; 12 keeps that rhythm across the web's responsive grid
 * (12 divides cleanly by 2, 3 and 4 columns → 6 / 4 / 3 rows per slice).
 */
export const PRODUCTS_PER_SLICE = 12;

export type HomeFeedSplit = {
	/** Blocks drawn as chrome above the grid (before the marker). */
	header: FeedBlockWire[];
	/** Blocks interleaved into the product grid (after the marker). */
	injected: FeedBlockWire[];
	/** The `personalFeed` marker itself, kept only for its «Моя лента» title. */
	personal?: FeedBlockWire;
};

/**
 * Split the feed at the `personalFeed` marker — same rule as the app's
 * `splitHomeFeedBlocks`. With no marker the whole list is header and nothing is
 * injected (the grid still renders as the page spine).
 */
export function splitHomeFeedBlocks(blocks: FeedBlockWire[]): HomeFeedSplit {
	const marker = blocks.findIndex((b) => b.kind === "personalFeed");
	if (marker === -1) return { header: blocks, injected: [] };
	return {
		header: blocks.slice(0, marker),
		injected: blocks.slice(marker + 1),
		personal: blocks[marker],
	};
}

export type HomeGridSegment =
	| { type: "grid"; key: string; products: Product[] }
	| { type: "block"; key: string; block: FeedBlockWire };

/**
 * Interleave merchandised blocks into the product grid — the web equivalent of
 * the app's `buildHomeFeedRows`. A block is placed after every
 * `PRODUCTS_PER_SLICE` products; blocks left over once the products run out are
 * appended so the server's merchandising is never silently dropped (a short,
 * narrowly-filtered feed would otherwise lose it).
 */
export function buildHomeGridSegments(
	products: Product[],
	injected: FeedBlockWire[],
	sliceSize = PRODUCTS_PER_SLICE,
): HomeGridSegment[] {
	const segments: HomeGridSegment[] = [];
	let nextBlock = 0;

	for (let start = 0; start < products.length; start += sliceSize) {
		const chunk = products.slice(start, start + sliceSize);
		segments.push({ type: "grid", key: `grid:${start}`, products: chunk });
		const block = injected[nextBlock];
		if (block) {
			segments.push({ type: "block", key: `block:${block.id}`, block });
			nextBlock++;
		}
	}

	while (nextBlock < injected.length) {
		const block = injected[nextBlock++];
		if (block) segments.push({ type: "block", key: `block:${block.id}`, block });
	}

	return segments;
}

/** Resolve a `productRail` block's ids against an already-loaded product pool. */
export function resolveRailProducts(
	productIds: string[] | undefined,
	pool: Map<string, Product>,
): Product[] {
	return (productIds ?? [])
		.map((id) => pool.get(id))
		.filter((p): p is Product => p != null);
}
